import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Agency } from '../types/agency';

export function useAgencyStats() {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = async (startDate: string, endDate: string) => {
    try {
      setLoading(true);

      // Récupérer l'année fiscale à partir de la date de début
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      const fiscalYear = startDateObj.getMonth() + 1 < 4 
        ? startDateObj.getFullYear() - 1 
        : startDateObj.getFullYear();

      console.log('📅 [useAgencyStats] Loading data for period:', { startDate, endDate, fiscalYear });

      // 1. Charger les agences avec leurs objectifs
      const { data: agenciesData, error: agenciesError } = await supabase
        .from('agencies')
        .select(`
          name,
          company,
          has_custom_objectives,
          monthly_objectives,
          agency_annual_objectives!left (
            fiscal_year,
            annual_objective
          )
        `)
        .eq('active', true)
        .order('company')
        .order('name');
      
      if (agenciesError) throw agenciesError;

      // 2. Charger TOUS les négociateurs avec leurs agences et dates
      const { data: negotiatorsData, error: negotiatorsError } = await supabase
        .from('negotiators')
        .select(`
          id,
          firstname,
          lastname,
          status,
          role,
          arrival_date,
          departure_date,
          has_custom_objectives,
          monthly_objectives,
          negotiator_annual_objectives!left (
            fiscal_year,
            annual_objective
          ),
          negotiator_agencies (
            agency,
            start_date,
            end_date,
            objective_percentage
          )
        `)
        .eq('archived', false);
      
      if (negotiatorsError) throw negotiatorsError;

      // 3. Charger les transactions
      const { data: transactionsData, error: transactionsError } = await supabase
        .from('transactions')
        .select(`
          id,
          fees_without_tax,
          status,
          compromise_date,
          transaction_retrocessions (
            amount
          ),
          transaction_negotiators (
            negotiator_id,
            role,
            percentage,
            agency
          )
        `)
        .or(`and(status.eq.potentiel),and(status.in.(compromis,acte,encaisse,cloture),compromise_date.gte.${startDate},compromise_date.lte.${endDate})`);

      if (transactionsError) throw transactionsError;

      // 4. Calculer les statistiques par agence
      const agencyStats = new Map<string, {
        total_sales: number;
        potential_sales: number;
        sales_count: number;
        negotiators: Map<string, {
          total_sales: number;
          potential_sales: number;
          sales_count: number;
        }>;
      }>();

      // Initialiser les statistiques pour chaque agence
      agenciesData?.forEach(agency => {
        agencyStats.set(agency.name, {
          total_sales: 0,
          potential_sales: 0,
          sales_count: 0,
          negotiators: new Map()
        });
      });

      // Calculer les statistiques des transactions
      transactionsData?.forEach(transaction => {
        // Calculer les honoraires nets HT
        const totalRetrocessions = transaction.transaction_retrocessions?.reduce((sum, r) => sum + r.amount, 0) || 0;
        const netFeesWithoutTax = transaction.fees_without_tax - totalRetrocessions;

        console.log(`\n💰 [useAgencyStats] Transaction ${transaction.id}`);
        console.log('  - Fees without tax:', transaction.fees_without_tax);
        console.log('  - Total retrocessions:', totalRetrocessions);
        console.log('  - Net fees without tax:', netFeesWithoutTax);
        console.log('  - Status:', transaction.status);

        // Pré-calculer l'agence résolue pour chaque ligne transaction_negotiators
        // Priorité : tn.agency (si rempli), sinon déduit depuis negotiator_agencies
        const txDate = transaction.compromise_date ? new Date(transaction.compromise_date) : endDateObj;
        const resolvedAgency = new Map<string, string | null>(); // negotiator_id → agency

        transaction.transaction_negotiators.forEach(tn => {
          let agency = tn.agency;
          if (!agency) {
            const negotiator = negotiatorsData?.find(n => n.id === tn.negotiator_id);
            if (negotiator) {
              const activeAssoc = negotiator.negotiator_agencies.find(na => {
                const s = na.start_date ? new Date(na.start_date) : null;
                const e = na.end_date ? new Date(na.end_date) : null;
                return (!s || s <= txDate) && (!e || e >= txDate);
              });
              agency = activeAssoc?.agency || null;
            }
          }
          resolvedAgency.set(tn.negotiator_id, agency);
        });

        // Calculer les honoraires par agence en fonction des pourcentages des négociateurs
        const agencyShares = new Map<string, number>();

        transaction.transaction_negotiators.forEach(tn => {
          const negotiatorAgency = resolvedAgency.get(tn.negotiator_id);

          console.log(`    🔎 Negotiator: ${tn.negotiator_id}`);
          console.log(`       - Agency: ${negotiatorAgency}`);
          console.log(`       - Percentage: ${tn.percentage}%`);

          if (negotiatorAgency) {
            const currentShare = agencyShares.get(negotiatorAgency) || 0;
            agencyShares.set(negotiatorAgency, currentShare + tn.percentage);
            console.log(`       - Updated agency share for ${negotiatorAgency}: ${currentShare + tn.percentage}%`);
          }
        });

        console.log('  - Agency shares:', Array.from(agencyShares.entries()));

        // Agences où un négociateur "vendeur" ou "vendeur/apporteur" est intervenu
        // → seules ces transactions comptent comme une vente (Nb Transactions)
        const sellingAgencies = new Set<string>();
        transaction.transaction_negotiators.forEach(tn => {
          if (tn.role === 'vendeur' || tn.role === 'vendeur_apporteur') {
            const negotiatorAgency = resolvedAgency.get(tn.negotiator_id);
            if (negotiatorAgency) sellingAgencies.add(negotiatorAgency);
          }
        });

        // Distribuer les honoraires aux agences selon les parts calculées
        agencyShares.forEach((totalPercentage, agencyName) => {
          const agencyData = agencyStats.get(agencyName);
          if (agencyData) {
            // Calculer la part de l'agence
            const agencyAmount = (netFeesWithoutTax * totalPercentage) / 100;

            console.log(`  📊 Agency ${agencyName}:`);
            console.log(`     - Total percentage: ${totalPercentage}%`);
            console.log(`     - Agency amount: ${agencyAmount}`);

            if (transaction.status === 'potentiel') {
              agencyData.potential_sales += agencyAmount;
              console.log(`     - Added to potential_sales`);
            } else {
              agencyData.total_sales += agencyAmount;
              if (sellingAgencies.has(agencyName)) {
                agencyData.sales_count++;
              }
              console.log(`     - Added to total_sales`);
            }

            // Statistiques par négociateur dans cette agence
            transaction.transaction_negotiators.forEach(tn => {
              if (resolvedAgency.get(tn.negotiator_id) === agencyName) {
                const negotiatorAmount = (netFeesWithoutTax * tn.percentage) / 100;
                const negotiatorStats = agencyData.negotiators.get(tn.negotiator_id) || {
                  total_sales: 0,
                  potential_sales: 0,
                  sales_count: 0
                };

                console.log(`     👤 Negotiator ${tn.negotiator_id}:`);
                console.log(`        - Percentage: ${tn.percentage}%`);
                console.log(`        - Amount: ${negotiatorAmount}`);

                if (transaction.status === 'potentiel') {
                  negotiatorStats.potential_sales += negotiatorAmount;
                  console.log(`        - Added to potential_sales`);
                } else {
                  negotiatorStats.total_sales += negotiatorAmount;
                  if (tn.role === 'vendeur' || tn.role === 'vendeur_apporteur') {
                    negotiatorStats.sales_count++;
                  }
                  console.log(`        - Added to total_sales (new total: ${negotiatorStats.total_sales})`);
                }

                agencyData.negotiators.set(tn.negotiator_id, negotiatorStats);
              }
            });
          } else {
            console.log(`  ⚠️ Agency ${agencyName} not found in agencyStats!`);
          }
        });
      });

      console.log('\n📈 [useAgencyStats] Final agency stats:');
      agencyStats.forEach((stats, agencyName) => {
        console.log(`\n  Agency: ${agencyName}`);
        console.log(`  - Total sales: ${stats.total_sales}`);
        console.log(`  - Potential sales: ${stats.potential_sales}`);
        console.log(`  - Sales count: ${stats.sales_count}`);
        console.log(`  - Negotiators:`, stats.negotiators.size);
      });

      // 5. Fonction pour calculer l'objectif d'un négociateur pour une période donnée
      const calculateNegotiatorPeriodObjective = (negotiator: any) => {
        let periodObjective = 0;

        console.log(`\n🎯 [calculateNegotiatorPeriodObjective] Negotiator: ${negotiator.firstname} ${negotiator.lastname}`);
        console.log('  - has_custom_objectives:', negotiator.has_custom_objectives);

        // Si objectifs mensuels personnalisés
        if (negotiator.has_custom_objectives && negotiator.monthly_objectives?.[fiscalYear]) {
          const monthlyObjectives = negotiator.monthly_objectives[fiscalYear];
          console.log('  - Using custom monthly objectives for year:', fiscalYear);
          
          // Parcourir tous les mois de la période
          let currentMonth = new Date(startDateObj.getFullYear(), startDateObj.getMonth(), 1);
          const lastMonth = new Date(endDateObj.getFullYear(), endDateObj.getMonth(), 1);
          
          while (currentMonth <= lastMonth) {
            const monthKey = String(currentMonth.getMonth() + 1).padStart(2, '0');
            const monthObjective = monthlyObjectives[monthKey] || 0;
            periodObjective += monthObjective;
            
            console.log(`  - Month ${monthKey}: ${monthObjective} € (total: ${periodObjective} €)`);
            
            currentMonth.setMonth(currentMonth.getMonth() + 1);
          }
        } else {
          // Sinon utiliser l'objectif annuel standard et le proratiser
          const annualObjective = negotiator.negotiator_annual_objectives?.find(
            (obj: any) => obj.fiscal_year === fiscalYear
          );
          const fullAnnualObjective = annualObjective?.annual_objective || 0;
          
          console.log('  - Using standard annual objective:', fullAnnualObjective);
          
          // Calculer le nombre de mois dans la période
          const monthsDiff = (endDateObj.getFullYear() - startDateObj.getFullYear()) * 12 
            + (endDateObj.getMonth() - startDateObj.getMonth()) + 1;
          
          console.log('  - Months in period:', monthsDiff);
          
          // Proratiser l'objectif annuel
          periodObjective = (fullAnnualObjective / 12) * monthsDiff;
          
          console.log('  - Prorated objective:', periodObjective);
        }

        console.log('  ✅ Final period objective:', periodObjective);
        return periodObjective;
      };

      // 6. Fonction pour calculer l'objectif d'une agence pour une période donnée
      const calculateAgencyPeriodObjective = (agency: any) => {
        let periodObjective = 0;

        console.log(`\n🎯 [calculateAgencyPeriodObjective] Agency: ${agency.name}`);
        console.log('  - has_custom_objectives:', agency.has_custom_objectives);

        // Si objectifs mensuels personnalisés
        if (agency.has_custom_objectives && agency.monthly_objectives?.[fiscalYear]) {
          const monthlyObjectives = agency.monthly_objectives[fiscalYear];
          console.log('  - Using custom monthly objectives for year:', fiscalYear);
          
          // Parcourir tous les mois de la période
          let currentMonth = new Date(startDateObj.getFullYear(), startDateObj.getMonth(), 1);
          const lastMonth = new Date(endDateObj.getFullYear(), endDateObj.getMonth(), 1);
          
          while (currentMonth <= lastMonth) {
            const monthKey = String(currentMonth.getMonth() + 1).padStart(2, '0');
            const monthObjective = monthlyObjectives[monthKey] || 0;
            periodObjective += monthObjective;
            
            console.log(`  - Month ${monthKey}: ${monthObjective} € (total: ${periodObjective} €)`);
            
            currentMonth.setMonth(currentMonth.getMonth() + 1);
          }
        } else {
          // Sinon utiliser l'objectif annuel standard et le proratiser
          const objective = agency.agency_annual_objectives?.find(
            (obj: any) => obj.fiscal_year === fiscalYear
          );
          const fullAnnualObjective = objective?.annual_objective || 0;
          
          console.log('  - Using standard annual objective:', fullAnnualObjective);
          
          // Calculer le nombre de mois dans la période
          const monthsDiff = (endDateObj.getFullYear() - startDateObj.getFullYear()) * 12 
            + (endDateObj.getMonth() - startDateObj.getMonth()) + 1;
          
          console.log('  - Months in period:', monthsDiff);
          
          // Proratiser l'objectif annuel
          periodObjective = (fullAnnualObjective / 12) * monthsDiff;
          
          console.log('  - Prorated objective:', periodObjective);
        }

        console.log('  ✅ Final period objective:', periodObjective);
        return periodObjective;
      };

      // 7. Formater les données pour chaque agence
      const formattedAgencies = agenciesData.map(agency => {
        const stats = agencyStats.get(agency.name) || {
          total_sales: 0,
          potential_sales: 0,
          sales_count: 0,
          negotiators: new Map()
        };

        // Récupérer tous les négociateurs associés à cette agence (SAUF DIRECTION EKYO pour l'affichage)
        const agencyNegotiators = negotiatorsData
          .filter(negotiator => {
            // Exclure DIRECTION EKYO de la liste affichée
            if (negotiator.firstname === 'DIRECTION') {
              return false;
            }

            // Trouver l'association avec cette agence
            const agencyAssociation = negotiator.negotiator_agencies.find(na => na.agency === agency.name);
            if (!agencyAssociation) return false;

            // Dates de l'association agence-négociateur
            const assocStartDate = agencyAssociation.start_date ? new Date(agencyAssociation.start_date) : null;
            const assocEndDate = agencyAssociation.end_date ? new Date(agencyAssociation.end_date) : null;

            // Dates de la période consultée
            const periodStartDate = new Date(startDate);
            const periodEndDate = new Date(endDate);

            // Vérifier si l'association était active pendant la période consultée
            // L'association est active si elle se chevauche avec la période :
            // - Elle a commencé avant ou pendant la période (start_date <= periodEndDate)
            // - Elle n'est pas terminée OU elle s'est terminée pendant ou après la période (end_date >= periodStartDate)
            
            const startedBeforePeriodEnd = !assocStartDate || assocStartDate <= periodEndDate;
            const endedAfterPeriodStart = !assocEndDate || assocEndDate >= periodStartDate;

            const wasActiveDuringPeriod = startedBeforePeriodEnd && endedAfterPeriodStart;

            // Si le négociateur n'était pas actif pendant la période, on ne l'affiche pas
            if (!wasActiveDuringPeriod) {
              return false;
            }

            // Vérifier si le négociateur a des ventes dans cette agence pendant la période
            const negotiatorStats = stats.negotiators.get(negotiator.id);
            if (!negotiatorStats) {
              // Même sans ventes, on l'affiche s'il était actif pendant la période
              return true;
            }
            
            return true;
          })
          .map(negotiator => {
            const negotiatorStats = stats.negotiators.get(negotiator.id) || {
              total_sales: 0,
              potential_sales: 0,
              sales_count: 0
            };

            // Récupérer le pourcentage d'objectif pour cette agence
            const agencyAssociation = negotiator.negotiator_agencies.find(na => na.agency === agency.name);
            const objectivePercentage = agencyAssociation?.objective_percentage ?? 100;

            // Calculer l'objectif du négociateur pour la période
            const fullNegotiatorObjective = calculateNegotiatorPeriodObjective(negotiator);
            
            // Appliquer le pourcentage pour cette agence
            const agencyNegotiatorObjective = (fullNegotiatorObjective * objectivePercentage) / 100;

            return {
              id: negotiator.id,
              firstname: negotiator.firstname,
              lastname: negotiator.lastname,
              status: negotiator.status,
              total_sales: negotiatorStats.total_sales,
              sales_count: negotiatorStats.sales_count,
              annual_objective: agencyNegotiatorObjective,
              objective_percentage: objectivePercentage
            };
          });

        // Calculer l'objectif de l'agence en sommant les objectifs pondérés des négociateurs
        const agencyObjectiveFromNegotiators = agencyNegotiators.reduce((sum, neg) => {
          return sum + (neg.annual_objective || 0);
        }, 0);

        // Utiliser l'objectif de l'agence s'il existe, sinon utiliser la somme des objectifs des négociateurs
        let periodObjective = calculateAgencyPeriodObjective(agency);
        
        // Si l'agence n'a pas d'objectif défini, utiliser la somme des objectifs des négociateurs
        if (periodObjective === 0 && agencyObjectiveFromNegotiators > 0) {
          periodObjective = agencyObjectiveFromNegotiators;
        }

        const completion_rate = periodObjective ? (stats.total_sales / periodObjective) * 100 : 0;
        
        return {
          name: agency.name,
          company: agency.company,
          annual_objective: periodObjective,
          total_sales: stats.total_sales,
          remaining: periodObjective - stats.total_sales,
          completion_rate,
          negotiator_count: agencyNegotiators.length,
          sales_count: stats.sales_count,
          potential_sales: stats.potential_sales,
          negotiators: agencyNegotiators
        };
      });

      // 8. Trier les agences par CA réalisé
      const sortedAgencies = formattedAgencies.sort((a, b) => b.total_sales - a.total_sales);
      setAgencies(sortedAgencies);
    } catch (err: any) {
      console.error('Error in useAgencyStats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { agencies, loading, error, loadData };
}